import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';
import { 
  Shield, 
  LayoutDashboard, 
  Building2, 
  Users, 
  Database, 
  Settings,
  FileText,
  LogOut,
  ChevronLeft,
  Crown,
  Store,
  Plug,
  DollarSign,
  Bell,
  Handshake
} from 'lucide-react';

const Sidebar = ({ isOpen, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { superAdmin, logout } = useSuperAdminAuth();

  const menuItems = [
    {
      icon: LayoutDashboard,
      label: 'Dashboard',
      path: '/superadmin-portal/dashboard',
      description: 'Overview & Statistics'
    },
    {
      icon: Crown,
      label: 'Control Tower',
      path: '/superadmin-portal/dashboard/control-tower',
      description: 'Global Platform Management'
    },
    {
      icon: Bell,
      label: 'Status & Updates Management',
      path: '/superadmin-portal/dashboard/status-updates',
      description: 'Create, schedule, publish & resolve notices → Business Web, Android & iOS'
    },
    {
      icon: DollarSign,
      label: 'Commercial Control',
      path: '/superadmin-portal/dashboard/commercial',
      description: 'USD plans, features, integrations, add-ons'
    },
    {
      icon: Building2,
      label: 'Tenant Management',
      path: '/superadmin-portal/dashboard/tenants',
      description: 'Manage Business Tenants'
    },
    {
      icon: Users,
      label: 'User Management',
      path: '/superadmin-portal/dashboard/users',
      description: 'Platform Users'
    },
    {
      icon: Database,
      label: 'Taxonomy Manager',
      path: '/superadmin-portal/dashboard/taxonomy',
      description: 'Industries & Categories'
    },
    {
      icon: Database,
      label: 'Industry Profiles',
      path: '/superadmin-portal/dashboard/industry-profiles',
      description: 'Movers & field-service engine config'
    },
    {
      icon: Shield,
      label: 'Legal / Jurisdiction Policies',
      path: '/superadmin-portal/dashboard/legal-policies',
      description: 'Governed retention values by jurisdiction'
    },
    {
      icon: Store,
      label: 'Marketplace Control',
      path: '/superadmin-portal/dashboard/marketplace',
      description: 'Platform Marketplace'
    },
    {
      icon: Handshake,
      label: 'Partners & Channels',
      path: '/superadmin-portal/dashboard/partners',
      description: 'Partner Program Governance'
    },
    {
      icon: Shield,
      label: 'Security & Compliance',
      path: '/superadmin-portal/dashboard/security',
      description: 'Security Management'
    },
    {
      icon: Plug,
      label: 'Integration Governance',
      path: '/superadmin-portal/dashboard/integration-governance',
      description: 'Platform Connector Frameworks'
    },
    {
      icon: FileText,
      label: 'Audit Logs',
      path: '/superadmin-portal/dashboard/audit-logs',
      description: 'Security & Activity Logs'
    },
    {
      icon: Settings,
      label: 'Settings',
      path: '/superadmin-portal/dashboard/settings',
      description: 'System Configuration'
    }
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/superadmin-portal/login');
  };

  if (!isOpen) {
    return (
      <div className="w-20 bg-gradient-to-b from-purple-900 to-purple-800 text-white flex flex-col">
        <div className="p-4 border-b border-purple-700 flex justify-center">
          <Shield className="h-8 w-8" />
        </div>
        <div className="flex-1 py-4">
          {menuItems.map((item, index) => (
            <button
              key={index}
              onClick={() => navigate(item.path)}
              className={`w-full p-4 hover:bg-purple-700 transition-colors flex justify-center ${
                location.pathname === item.path ? 'bg-purple-700' : ''
              }`}
              title={item.label}
            >
              <item.icon className="h-6 w-6" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-64 bg-gradient-to-b from-purple-900 to-purple-800 text-white flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-purple-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-700 rounded-lg">
              <Shield className="h-6 w-6" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Super Admin</h1>
              <p className="text-xs text-purple-300">Control Portal</p>
            </div>
          </div>
          {onToggle && (
            <button onClick={onToggle} className="p-1 hover:bg-purple-700 rounded transition-colors">
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-purple-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center font-bold">
            {superAdmin?.email?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{superAdmin?.email}</p>
            <p className="text-xs text-purple-300">System Administrator</p>
          </div>
        </div>
      </div>

      {/* Menu Items — min-h-0 lets this flex child shrink so overflow-y-auto engages and the
          logout footer below stays reachable on short viewports. */}
      <nav className="flex-1 min-h-0 py-4 overflow-y-auto">
        {menuItems.map((item, index) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={index}
              onClick={() => navigate(item.path)}
              className={`w-full px-4 py-3 flex items-center gap-3 transition-colors ${
                isActive
                  ? 'bg-purple-700 border-l-4 border-white'
                  : 'hover:bg-purple-700/50 border-l-4 border-transparent'
              }`}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              <div className="flex-1 text-left">
                <p className="text-sm font-medium">{item.label}</p>
                <p className="text-xs text-purple-300">{item.description}</p>
              </div>
            </button>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-purple-700">
        <button
          onClick={handleLogout}
          className="w-full px-4 py-3 bg-red-600 hover:bg-red-700 rounded-lg flex items-center justify-center gap-2 transition-colors"
        >
          <LogOut className="h-5 w-5" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;